import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function LandlordDashboard() {
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  // const [syndications, setSyndications] = useState([]);
  const [subscription, setSubscription] = useState({ subscription_status: 'free' });
  const [, setPropertyCount] = useState(0);
  const [, setUpgrading] = useState(false);
  const [verification, setVerification] = useState(null);
  const [, setVerificationLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState({ connected: false });
  const [paymentsSettings, setPaymentsSettings] = useState({ payments_enabled: false });
  const [archivedProperties, setArchivedProperties] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [promoCode] = useState('');
  const [, setPromoMessage] = useState('');
  const [primaryPropertyId, setPrimaryPropertyId] = useState(null);
  const [, setPrimaryMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setSubscriptionNotice] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [, setDismissStripeNotice] = useState(false);
  const [propertiesUnread, setPropertiesUnread] = useState(0);
  const location = useLocation();
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_landlord_properties') === '1');
  const [shareLinks, setShareLinks] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_API_BASE_URL}${url}`;
  };

  const statusLabels = {
    available: 'Available',
    off_market: 'Off market',
    occupied: 'Occupied'
  };


  useEffect(() => {
    const fetchLandlordProperties = async () => {
      const token = localStorage.getItem('token');
      const userType = localStorage.getItem('userType');

      if (!token || userType !== 'landlord') {
        navigate('/login'); // Redirect to login if not authenticated as landlord
        return;
      }

      try {
        const [propertiesRes, archivedRes, inquiriesRes, subscriptionRes, countRes, verificationRes, connectRes, paymentsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties?archived=1`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/inquiries`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/unread-count`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/payments/settings`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })
        ]);

        if (!propertiesRes.ok) {
          if (propertiesRes.status === 401 || propertiesRes.status === 403) {
            // Token expired or unauthorized, clear storage and redirect
            localStorage.removeItem('token');
            localStorage.removeItem('userType');
            localStorage.removeItem('userId');
            alert('Session expired or unauthorized. Please log in again.');
            navigate('/login');
          }
          throw new Error(`HTTP error! status: ${propertiesRes.status}`);
        }
        const propertiesData = await propertiesRes.json();
        const archivedData = archivedRes.ok ? await archivedRes.json() : { properties: [] };
        const inquiriesData = inquiriesRes.ok ? await inquiriesRes.json() : { inquiries: [] };
        // syndication fetch removed
        const subscriptionData = subscriptionRes.ok ? await subscriptionRes.json() : { subscription: { subscription_status: 'free' }, propertyCount: 0 };
        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setPropertiesUnread(countData.count || 0);
          setLastViewedAt(countData.last_viewed_at || null);
        }
        const verificationData = verificationRes.ok ? await verificationRes.json().catch(() => ({})) : {};
        const connectData = connectRes.ok ? await connectRes.json().catch(() => ({})) : { connected: false };
        const paymentsData = paymentsRes.ok ? await paymentsRes.json().catch(() => ({})) : { settings: {} };
        setVerification(verificationData.verification || null);
        setVerificationLoading(false);
        setConnectStatus(connectData || { connected: false });
        setPaymentsSettings(paymentsData.settings || {});
        const loadedProps = propertiesData.properties || [];
        setProperties(loadedProps);
        // Pre-populate share links for properties that already have tokens
        const existingLinks = {};
        loadedProps.forEach(p => {
          if (p.share_token) existingLinks[p.id] = `${window.location.origin}/properties/${p.id}?share=${p.share_token}`;
        });
        if (Object.keys(existingLinks).length > 0) setShareLinks(prev => ({ ...prev, ...existingLinks }));
        setArchivedProperties(archivedData.properties || []);
        setInquiries(inquiriesData.inquiries || []);
        // syndication state removed
        const subscriptionInfo = subscriptionData.subscription || { subscription_status: 'free' };
        setSubscription(subscriptionInfo);
        setPropertyCount(subscriptionData.propertyCount || 0);
        setPrimaryPropertyId(subscriptionInfo.primary_property_id || null);

        if (token) {
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/properties/mark-viewed`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
          window.dispatchEvent(new Event('properties:viewed'));
        }
      } catch (err) {
        setError(err.message);
        setVerificationLoading(false);
        console.error("Error fetching landlord properties:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLandlordProperties();
  }, [navigate, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');
    const canceled = params.get('canceled');

    if (canceled) {
      setSubscriptionNotice('Subscription checkout canceled. You can upgrade anytime.');
      navigate(window.location.pathname, { replace: true });
      return;
    }

    if (!success || !sessionId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const confirmSubscription = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sessionId })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Failed to confirm subscription');
        }
        const subRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const subData = await subRes.json().catch(() => ({}));
        if (subRes.ok && subData.subscription) {
          setSubscription(subData.subscription);
        }
        setSubscriptionNotice('Subscription activated. Welcome to Pro.');
        navigate(window.location.pathname, { replace: true });
      } catch (err) {
        alert(err.message || 'Failed to confirm subscription');
      }
    };

    confirmSubscription();
  }, [navigate]);

  const handleArchiveProperty = async (propertyId) => {
    if (!window.confirm('Archive this property?')) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${propertyId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json().catch(() => ({}));
      const archived = data.property;
      if (archived) {
        setProperties((prev) => prev.filter((p) => p.id !== propertyId));
        setArchivedProperties((prev) => [archived, ...prev]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error archiving property:', err);
      alert(`Error archiving property: ${err.message}`);
    }
  };

  const handleUnarchiveProperty = async (propertyId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${propertyId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json().catch(() => ({}));
      const restored = data.property;
      if (restored) {
        setArchivedProperties((prev) => prev.filter((p) => p.id !== propertyId));
        setProperties((prev) => [restored, ...prev]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error unarchiving property:', err);
      alert(`Error unarchiving property: ${err.message}`);
    }
  };

  const handleStatusChange = async (propertyId, status) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${propertyId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.status === 402) {
        alert(data.error || 'Upgrade to change status for this property.');
        return;
      }
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      const updated = data.property;
      if (updated) {
        setProperties((prev) => prev.map((p) => (p.id === propertyId ? updated : p)));
      }
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

if (loading) {
    return <main className="App-main">Loading dashboard...</main>;
  }

  if (error) {
    return <main className="App-main"><p className="error-message">Error: {error}</p></main>;
  }

  const subscriptionActive = subscription?.subscription_status === 'active';
  // eslint-disable-next-line no-unused-vars
  const paymentsReady = subscriptionActive && !!paymentsSettings?.payments_enabled && !!connectStatus?.connected;
  const fallbackPrimary = !subscriptionActive && properties.length > 0
    ? properties.reduce((prev, curr) => {
        const prevDate = prev.created_at ? new Date(prev.created_at) : new Date(0);
        const currDate = curr.created_at ? new Date(curr.created_at) : new Date(0);
        return currDate < prevDate ? curr : prev;
      })
    : null;
  const activePrimaryId = primaryPropertyId || fallbackPrimary?.id || null;
  const isLockedProperty = (property) => !subscriptionActive && properties.length > 1 && activePrimaryId && property.id !== activePrimaryId;

  // eslint-disable-next-line no-unused-vars
  const handleUpgrade = async (plan = 'monthly') => {
    const token = localStorage.getItem('token');
    setUpgrading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to upgrade subscription');
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setSubscription(data.subscription || { subscription_status: 'active' });
    } catch (err) {
      alert(err.message || 'Failed to upgrade subscription');
    } finally {
      setUpgrading(false);
    }
  };


  // eslint-disable-next-line no-unused-vars
  const handleApplyPromo = async () => {
    const token = localStorage.getItem('token');
    if (!promoCode.trim()) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/promo/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to apply promo');
      setPromoMessage(`Promo applied: ${data.promo.code}`);
      const subRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const subData = await subRes.json().catch(() => ({}));
      if (subRes.ok) {
        setSubscription(subData.subscription || { subscription_status: 'active' });
        setPropertyCount(subData.propertyCount || 0);
        setPrimaryPropertyId(subData.subscription?.primary_property_id || null);
      }
    } catch (err) {
      setPromoMessage(err.message || 'Failed to apply promo');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSetPrimaryProperty = async (propertyId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/primary-property`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ propertyId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to set primary property');
      setPrimaryPropertyId(data.primary_property_id || propertyId);
      setPrimaryMessage('Primary property updated.');
    } catch (err) {
      setPrimaryMessage(err.message || 'Failed to set primary property');
    }
  };

  const handleShareLink = async (propertyId) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties/${propertyId}/share-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create share link');
      }
      if (data.shareUrl) {
        setShareLinks(prev => ({ ...prev, [propertyId]: data.shareUrl }));
      }
    } catch (err) {
      alert(err.message || 'Failed to create share link');
    }
  };

  const handleCopyLink = async (propertyId) => {
    const url = shareLinks[propertyId];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(propertyId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopiedId(propertyId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const now = new Date();
  const upcomingViewings = (inquiries || [])
    .filter((inq) => inq.status === 'scheduled' && inq.walkthrough_start && new Date(inq.walkthrough_start) >= now)
    .sort((a, b) => new Date(a.walkthrough_start) - new Date(b.walkthrough_start));
  const nextViewing = upcomingViewings[0];

  const latestProperties = [...properties]
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))
    .slice(0, 3);

  // eslint-disable-next-line no-unused-vars
  const isVerified = verification?.id_status === 'verified';

  return (
    <main className="App-main landlord-dashboard-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Properties</p>
          <h2>My Properties {propertiesUnread > 0 && <span className="nav-badge">{propertiesUnread}</span>}</h2>
          <p>Manage listings, availability, and share links.</p>
        </div>
        <div className="ledger-actions">
          <Link to="/landlord/properties/new" className="primary-button add-property-button">Add New Property</Link>
        </div>
      </div>

      {subscription?.is_blocked && (
        <div className="dashboard-section notice-card" style={{ marginTop: '1rem', borderLeft: '4px solid #d93025', background: '#fff5f5' }}>
          <strong>Account blocked.</strong>
          <p className="status-text">Your account is blocked from creating or updating listings. Please contact support for review.</p>
        </div>
      )}

      {!hideNewItems && ((propertiesUnread > 0 && latestProperties.length > 0) || upcomingViewings.length > 0) && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', marginTop: '1rem' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest property activity</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_landlord_properties', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestProperties.length === 0 && upcomingViewings.length === 0 ? (
          <p className="status-text">No new activity yet.</p>
        ) : (
          <div className="inquiry-list">
            {upcomingViewings.length > 0 && (
              <div className="inquiry-card" key="upcoming-viewings">
                <p><strong>Upcoming viewings</strong></p>
                <p className="status-text">Scheduled: {upcomingViewings.length}</p>
                <p className="timestamp">Next: {nextViewing?.walkthrough_start ? new Date(nextViewing.walkthrough_start).toLocaleString() : '-'}</p>
                <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                  <Link className="secondary-button" to="/landlord/analytics#viewings">View schedule</Link>
                </div>
              </div>
            )}
            {latestProperties.map((property) => {
              const updatedAt = property.updated_at || property.created_at;
              const isNew = lastViewedAt && updatedAt && new Date(updatedAt) > new Date(lastViewedAt);
              return (
                <div key={`latest-${property.id}`} className="inquiry-card">
                  <p><strong>{property.address}{property.unit ? `, Unit ${property.unit}` : ''}</strong></p>
                  <p className="status-text">Status: {property.status}</p>
                  <p className="timestamp">{updatedAt ? new Date(updatedAt).toLocaleString() : '-'}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    {isLockedProperty(property) ? (
                      <span className="status-text" title="Free plan allows only 1 editable property">Locked on free plan</span>
                    ) : (
                      <Link className="secondary-button" to={`/landlord/properties/edit/${property.id}`}>Edit</Link>
                    )}
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <div className="property-management-grid">
        {properties.length > 0 ? (
          properties.map((property) => (
            <div key={property.id} className="landlord-property-card">
              {property.photo_urls && property.photo_urls.length > 0 && (
                <div>
                  <img src={resolveImageUrl(property.photo_urls[0])} alt={property.address} className="landlord-property-image" />
                  <div className="property-thumb-row">
                    {property.photo_urls.slice(1, 5).map((url, idx) => (
                      <img
                        key={idx}
                        src={resolveImageUrl(url)}
                        alt={`${property.address} thumbnail ${idx + 2}`}
                        className="property-thumb-small"
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="landlord-property-info">
                <p className="eyebrow-tag">Status: <strong>{statusLabels[property.status] || property.status}</strong>{isLockedProperty(property) && <span className="report-chip" style={{ marginLeft: '0.5rem' }} title="Free plan allows only 1 editable property">Locked</span>}</p>
                <h3>{property.address}{property.unit ? `, Unit ${property.unit}` : ''}, {property.city}</h3>
                <p className="landlord-property-rent">${property.rent}/month</p>
                <p>{property.bedrooms} Bed | {property.bathrooms} Bath</p>
                <p>Occupancy: <strong>{property.is_occupied ? 'occupied' : 'vacant'}</strong></p>
                {property.upcoming_viewing_slots > 0 && (
                  <p><span className="report-chip report-chip--success" style={{ fontSize: '0.75rem' }}>{property.upcoming_viewing_slots} upcoming viewing{property.upcoming_viewing_slots !== 1 ? 's' : ''}</span></p>
                )}
                <div className="landlord-property-actions stacked-actions">
                  <select
                    value={property.status}
                    onChange={(e) => handleStatusChange(property.id, e.target.value)}
                    className="form-control"
                    disabled={isLockedProperty(property)}
                    title={isLockedProperty(property) ? 'Upgrade to change status for additional properties' : ''}
                  >
                    <option value="available">Available</option>
                    <option value="off_market">Off market</option>
                    <option value="occupied">Occupied</option>
                  </select>
                  {isLockedProperty(property) ? (
                    <span className="status-text" title="Free plan allows only 1 editable property">Upgrade to edit this property.</span>
                  ) : (
                    <>
                      <Link to={`/landlord/properties/edit/${property.id}`} className="secondary-button">Edit</Link>
                      <Link to={`/landlord/properties/edit/${property.id}?focus=walkthrough`} className="secondary-button">Manage Viewings</Link>
                      {shareLinks[property.id] ? (
                        <div style={{ flex: '1 1 100%', marginTop: '0.25rem' }}>
                          <label className="status-text" style={{ display: 'block', fontSize: '0.78rem', marginBottom: '0.25rem', fontWeight: 600 }}>Invitation link</label>
                          <input
                            type="text"
                            readOnly
                            value={shareLinks[property.id]}
                            onClick={(e) => e.target.select()}
                            style={{ width: '100%', fontSize: '0.8rem', padding: '0.4rem 0.5rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--surface-color, #f5f5f5)', color: 'var(--text-color)', marginBottom: '0.35rem', boxSizing: 'border-box' }}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => handleCopyLink(property.id)}
                              className="primary-button"
                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                            >
                              {copiedId === property.id ? 'Copied!' : 'Copy link'}
                            </button>
                            <a
                              href={shareLinks[property.id]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="secondary-button"
                              style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', textDecoration: 'none' }}
                            >
                              Preview
                            </a>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => handleShareLink(property.id)} className="primary-button">Create Invitation Link</button>
                      )}
                      {/* Share buttons */}
                      {shareLinks[property.id] && (
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', flex: '1 1 100%', marginTop: '0.25rem' }}>
                          <a className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', textDecoration: 'none' }}
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLinks[property.id])}`}
                            target="_blank" rel="noopener noreferrer">Facebook</a>
                          <a className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', textDecoration: 'none' }}
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLinks[property.id])}&text=${encodeURIComponent(`${property.address}, ${property.city} — $${property.rent}/mo`)}`}
                            target="_blank" rel="noopener noreferrer">X</a>
                          <a className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', textDecoration: 'none' }}
                            href={`https://wa.me/?text=${encodeURIComponent(`${property.address}, ${property.city} — $${property.rent}/mo ${shareLinks[property.id]}`)}`}
                            target="_blank" rel="noopener noreferrer">WhatsApp</a>
                        </div>
                      )}
                      <button onClick={() => handleArchiveProperty(property.id)} className="secondary-button">Archive</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>You have no properties listed yet. Add one to get started!</p>
        )}
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <button className="secondary-button" onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? 'Hide archived' : 'View archived'}
        </button>
      </div>

      {showArchived && (
        <div className="property-management-grid" style={{ marginTop: '1rem' }}>
          {archivedProperties.length > 0 ? (
            archivedProperties.map((property) => (
              <div key={property.id} className="landlord-property-card">
                {property.photo_urls && property.photo_urls.length > 0 && (
                  <div>
                    <img src={resolveImageUrl(property.photo_urls[0])} alt={property.address} className="landlord-property-image" />
                    <div className="property-thumb-row">
                      {property.photo_urls.slice(1, 5).map((url, idx) => (
                        <img
                          key={idx}
                          src={resolveImageUrl(url)}
                          alt={`${property.address} thumbnail ${idx + 2}`}
                          className="property-thumb-small"
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="landlord-property-info">
                  <p className="eyebrow-tag">Archived</p>
                  <h3>{property.address}{property.unit ? `, Unit ${property.unit}` : ''}, {property.city}</h3>
                  <p className="landlord-property-rent">${property.rent}/month</p>
                  <p>{property.bedrooms} Bed | {property.bathrooms} Bath</p>
                  <div className="landlord-property-actions stacked-actions">
                    <button onClick={() => handleUnarchiveProperty(property.id)} className="secondary-button">Restore</button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="status-text">No archived properties.</p>
          )}
        </div>
      )}
    </main>
  );
}

export default LandlordDashboard;





































